import React, { useRef, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { MapPin, Loader2 } from 'lucide-react';
import { useGoogleMaps } from '../hooks/useGoogleMaps';

/**
 * Address autocomplete component using Google Places API PlaceAutocompleteElement
 * with character-by-character simulation for OCR values
 * @param {Object} props
 * @param {string} props.label - Input label
 * @param {string} props.value - Input value
 * @param {Function} props.onChange - Change handler
 * @param {Function} props.onCoordinatesChange - Callback for lat/lng changes
 * @param {Function} props.onAddressComponentsChange - Callback for structured address data
 * @param {string} props.error - Validation error message
 * @param {string} props.apiKey - Google Maps API key
 */
const AddressAutocomplete = ({ label, value, onChange, onCoordinatesChange, onAddressComponentsChange, error, apiKey }) => {
    const manualInputRef = useRef(null);
    const dropdownRef = useRef(null);
    const isUserSelectionRef = useRef(false);
    const [inputValue, setInputValue] = useState(value || '');
    const [isSimulating, setIsSimulating] = useState(false);
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });

    // Load Google Maps script
    const isGoogleMapsLoaded = useGoogleMaps(apiKey);

    // Simulate character-by-character typing to trigger Google Places autocomplete
    const simulateTyping = async (text) => {
        if (!text || !isGoogleMapsLoaded || !window.google?.maps?.places?.AutocompleteSuggestion) return;

        setIsSimulating(true);

        try {
            // Use the new AutocompleteSuggestion API
            const { suggestions } = await window.google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
                input: text,
                includedRegionCodes: ['be', 'fr', 'nl', 'de', 'lu']
            });

            if (suggestions && suggestions.length > 0) {
                // Convert to format compatible with our UI
                const formattedSuggestions = suggestions.slice(0, 5).map(suggestion => ({
                    place_id: suggestion.placePrediction?.placeId || Math.random().toString(),
                    description: suggestion.placePrediction?.text?.text || ''
                }));

                setSuggestions(formattedSuggestions);
                setShowSuggestions(true);
            }
            setIsSimulating(false);
        } catch (error) {
            console.error('Error getting place predictions:', error);
            setIsSimulating(false);
        }
    };

    // Update internal state when external value changes
    useEffect(() => {
        // Skip update if the change came from user selection
        if (isUserSelectionRef.current) {
            console.log('Skipping useEffect update - user selection in progress');
            isUserSelectionRef.current = false;
            return;
        }

        const newValue = value || '';
        console.log('AddressAutocomplete useEffect - value from parent:', newValue, 'current inputValue:', inputValue);

        // Always sync with parent value
        setInputValue(newValue);

        // If there's a pre-filled value from OCR, simulate typing to get suggestions
        if (newValue.trim() !== '' && newValue !== inputValue && isGoogleMapsLoaded) {
            setTimeout(() => {
                simulateTyping(newValue);
            }, 500);
        }
    }, [value, isGoogleMapsLoaded]);

    // Handle suggestion selection
    const handleSuggestionClick = async (suggestion) => {
        const selectedAddress = suggestion.description;

        console.log('=== SUGGESTION CLICKED ===');
        console.log('Selected address from Google Places:', selectedAddress);

        // Hide suggestions dropdown first
        setShowSuggestions(false);
        setSuggestions([]);

        // Mark that this change came from user selection to prevent useEffect from overwriting
        isUserSelectionRef.current = true;

        // Update local state immediately to show the selected address
        setInputValue(selectedAddress);
        console.log('Local state updated to:', selectedAddress);

        // Notify parent component of the change
        if (onChange) {
            console.log('Calling onChange with:', selectedAddress);
            onChange('address', selectedAddress);
        } else {
            console.warn('onChange callback is not defined!');
        }

        // Fetch place details to get coordinates and address components
        if (suggestion.place_id && window.google?.maps?.places?.Place) {
            try {
                const place = new window.google.maps.places.Place({
                    id: suggestion.place_id
                });

                await place.fetchFields({
                    fields: ['location', 'addressComponents', 'formattedAddress']
                });

                // Extract coordinates
                if (place.location) {
                    const lat = place.location.lat();
                    const lng = place.location.lng();

                    // Call the coordinates change handler
                    if (onCoordinatesChange) {
                        onCoordinatesChange(lat, lng);
                    }
                }

                // Extract and parse address components
                if (place.addressComponents && onAddressComponentsChange) {
                    const components = {};

                    place.addressComponents.forEach(component => {
                        const types = component.types;

                        if (types.includes('street_number')) {
                            components.number = component.longText;
                        }
                        if (types.includes('route')) {
                            components.street = component.longText;
                        }
                        if (types.includes('postal_code')) {
                            components.zip = component.longText;
                        }
                        if (types.includes('locality')) {
                            components.city = component.longText;
                        }
                        if (types.includes('country')) {
                            components.country = component.longText;
                            components.countryCode = component.shortText;
                        }
                    });

                    console.log('Parsed address components:', components);

                    // Call the address components change handler
                    onAddressComponentsChange(components);
                }
            } catch (error) {
                console.error('Error fetching place details:', error);
            }
        }
    };

    // Handle manual input changes
    const handleManualInputChange = (e) => {
        const newValue = e.target.value;
        setInputValue(newValue);
        onChange('address', newValue);

        // Trigger suggestions as user types
        if (newValue && newValue.length > 2) {
            simulateTyping(newValue);
        } else {
            setShowSuggestions(false);
            setSuggestions([]);
        }
    };

    // Calculate dropdown position based on input element
    const updateDropdownPosition = () => {
        if (manualInputRef.current) {
            const rect = manualInputRef.current.getBoundingClientRect();
            setDropdownPosition({
                top: rect.bottom + window.scrollY + 4,
                left: rect.left + window.scrollX,
                width: rect.width
            });
        }
    };

    // Update position when suggestions appear
    useEffect(() => {
        if (showSuggestions) {
            updateDropdownPosition();
        }
    }, [showSuggestions]);

    // Close suggestions when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            // Check if click is outside both the input and the dropdown
            const clickedOutsideInput = manualInputRef.current && !manualInputRef.current.contains(event.target);
            const clickedOutsideDropdown = dropdownRef.current && !dropdownRef.current.contains(event.target);

            if (clickedOutsideInput && clickedOutsideDropdown) {
                console.log('Click outside detected - closing suggestions');
                setShowSuggestions(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
        <div className="relative">
            <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                <MapPin className="w-4 h-4 mr-2 text-cabaza-purple" />
                {label}
            </label>
            <div className="relative">
                <input
                    ref={manualInputRef}
                    type="text"
                    value={inputValue}
                    onChange={handleManualInputChange}
                    className={`w-full px-4 py-2 pr-10 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-cabaza-purple ${
                        error ? 'border-cabaza-pink' : 'border-gray-300'
                    }`}
                    placeholder={isGoogleMapsLoaded ? label : 'Chargement...'}
                    disabled={!isGoogleMapsLoaded}
                />
                {!isGoogleMapsLoaded && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />
                    </div>
                )}
            </div>

            {/* Suggestions dropdown - rendered via portal to escape modal overflow */}
            {showSuggestions && suggestions.length > 0 && createPortal(
                <div
                    ref={dropdownRef}
                    className="fixed z-[9999] bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto"
                    style={{
                        top: dropdownPosition.top,
                        left: dropdownPosition.left,
                        width: dropdownPosition.width
                    }}
                >
                    {suggestions.map((suggestion, index) => (
                        <div
                            key={suggestion.place_id}
                            onMouseDown={(e) => {
                                e.preventDefault(); // Prevent input blur
                                console.log('Suggestion mousedown - calling handleSuggestionClick');
                                handleSuggestionClick(suggestion);
                            }}
                            className={`px-4 py-3 cursor-pointer hover:bg-purple-50 ${
                                index !== 0 ? 'border-t border-gray-200' : ''
                            }`}
                        >
                            <div className="flex items-start">
                                <MapPin className="w-4 h-4 mr-2 mt-0.5 text-cabaza-purple flex-shrink-0" />
                                <span className="text-sm text-gray-900">{suggestion.description}</span>
                            </div>
                        </div>
                    ))}
                </div>,
                document.body
            )}

            {isSimulating && (
                <div className="absolute right-3 top-11 text-xs text-gray-500">
                    Recherche...
                </div>
            )}

            {error && <p className="mt-1 text-xs text-cabaza-pink">{error}</p>}
        </div>
    );
};

export default AddressAutocomplete;
